/**
 * Instant2D 拖动的输入、临时求解会话和提交边界。
 *
 * 同帧只预演最新位置；mouseup 先同步结算最终坐标再回滚预览并进入既有事务。
 * 取消、目标替换与释放仅回滚，不消费待处理坐标，也不新增模型历史。
 */
export class SketchDimensionInstant2dController {
	/** 当前拖动的唯一所有者。 */
	private state: SketchDimensionInstant2dState | null = null;
	/** 已释放的手势逐笔排队，不能像同一手势的 pointermove 一样覆盖。 */
	private readonly commits: SketchDimensionInstant2dCommit[] = [];
	/** 正式写回未完成时，后续预演只求值，不持有共享几何的回滚会话。 */
	private committing = false;
	/** 释放后解除宿主闭包，防止控制器引用保留整个 Viewer。 */
	private options: SketchDimensionInstant2dOptions | null;
	/** 全生命周期复用同一个帧回调，不为每次输入创建闭包。 */
	private readonly processQueuedInput = (): void => {
		this.consumePending();
	};

	/** @param options 求解、标注、历史与宿主帧端口。 */
	public constructor(options: SketchDimensionInstant2dOptions) {
		this.options = options;
	}

	/** @param dimensionId 拖动尺寸。 @param client 最新屏幕位置。 */
	public move(dimensionId: string, client: { readonly x: number; readonly y: number }): void {
		const options = this.options;
		if (!options) return;
		if (this.state && this.state.target.dimensionId !== dimensionId) this.cancel();
		if (!this.state) {
			const target = options.resolveTarget(dimensionId);
			if (!target) return;
			this.state = { target, client: { x: client.x, y: client.y }, pending: false, session: null, lastValue: null };
		}
		this.state.client.x = client.x;
		this.state.client.y = client.y;
		this.state.pending = true;
		options.scheduler.scheduleVisualUpdate(this, this.processQueuedInput);
	}

	/**
	 * 同步结算最终指针值，按释放顺序提交；历史等待不依赖下一帧。
	 * @param dimensionId 释放手柄的尺寸。
	 * @param client mouseup 的屏幕位置，不可退回上一帧已处理位置。
	 */
	public async commit(dimensionId: string, client: { readonly x: number; readonly y: number }): Promise<void> {
		const options = this.options;
		const state = this.state;
		if (!options || !state || state.target.dimensionId !== dimensionId) return;
		options.scheduler.cancelVisualUpdate(this);
		state.client.x = client.x;
		state.client.y = client.y;
		state.pending = true;
		this.consumePending();
		if (this.state !== state) return;
		const current = options.resolveTarget(dimensionId);
		const value = state.lastValue;
		// 先移除拖动所有权再回滚，防止标注清理回调递归取消或二次提交。
		this.cancel(dimensionId);
		if (!current || current.owner !== state.target.owner || value === null) return;
		// 是否无变化必须在该笔真正开始时判断：前一笔失败后名义值可能回退。
		return new Promise<void>((resolve, reject) => {
			this.commits.push({ target: state.target, value, resolve, reject });
			if (!this.committing) void this.drainCommits();
		});
	}

	/** @param dimensionId 可选目标；旧手柄的迟到取消不得影响另一个新拖动。 */
	public cancel(dimensionId?: string): void {
		const options = this.options;
		const state = this.state;
		if (!options || !state || (dimensionId !== undefined && state.target.dimensionId !== dimensionId)) return;
		options.scheduler.cancelVisualUpdate(this);
		this.state = null;
		state.pending = false;
		const session = state.session;
		state.session = null;
		try {
			session?.rollback();
		} finally {
			options.previewValue(state.target.dimensionId, null);
			options.refreshSketch(state.target.sketchId);
			options.requestRender();
		}
	}

	/** 文档/历史边界取消未提交输入；已进入正式事务的回滚仍由原事务服务负责。 */
	public reset(): void {
		try {
			this.cancel();
		} finally {
			// 先断开控制器持有的运行时目标，再结束排队 Promise，不保留旧文档。
			const cancelled = this.commits.splice(0);
			for (const request of cancelled) request.resolve();
		}
	}

	/** 停止未消费输入并恢复预览；必须先于草图及标注资源释放。 */
	public dispose(): void {
		try {
			this.reset();
		} finally {
			this.options = null;
		}
	}

	/**
	 * Instant2D 手势之间按释放顺序独占正式写回；单笔失败只拒绝该笔，不吞掉后续有效手势。
	 * 数值在释放时已经冻结，等待期间相机或前笔几何改变不得重新解释旧屏幕坐标。
	 */
	private async drainCommits(): Promise<void> {
		this.committing = true;
		try {
			while (this.commits.length > 0) {
				const request = this.commits.shift()!;
				const options = this.options;
				try {
					const current = options?.resolveTarget(request.target.dimensionId);
					if (
						options &&
						current?.owner === request.target.owner &&
						current.sketchId === request.target.sketchId &&
						Math.abs(request.value - current.nominalValue) > 1e-12
					) {
						try {
							await options.commitValue(current.sketchId, current.dimensionId, request.value);
						} finally {
							// 正式服务可能异步返回；不得向已替换的草图或释放后的标注刷新。
							if (this.options === options && options.resolveTarget(current.dimensionId)?.owner === current.owner) {
								options.refreshSketch(current.sketchId);
								options.requestRender();
								const state = this.state;
								if (state) {
									state.pending = true;
									options.scheduler.scheduleVisualUpdate(this, this.processQueuedInput);
								}
							}
						}
					}
					request.resolve();
				} catch (error) {
					// 返回对应 commit 的 Promise，由 Viewer 的既有错误通知出口报告。
					request.reject(error);
				}
			}
		} finally {
			this.committing = false;
		}
	}

	/** 宿主帧或提交边界消费一次输入；异常时释放任务，不让旧求解自动重试。 */
	private consumePending(): void {
		const options = this.options;
		const state = this.state;
		if (!options || !state?.pending) return;
		state.pending = false;
		const current = options.resolveTarget(state.target.dimensionId);
		if (!current || current.owner !== state.target.owner || current.sketchId !== state.target.sketchId) {
			this.cancel();
			return;
		}
		try {
			const previousSession = state.session;
			state.session = null;
			previousSession?.rollback();
			const preview = options.rehearse(current, state.client);
			if (preview.kind === "ready") {
				// rehearse 只计算快照；正式写回等待时不应用新快照，避免两个 rollback 共享几何。
				if (!this.committing) {
					// commit 失败也必须能回滚部分写回，因此先登记会话所有权。
					state.session = preview.session;
					preview.session?.commit();
				}
				state.lastValue = preview.value;
				options.previewValue(state.target.dimensionId, preview.value);
			}
			options.refreshSketch(state.target.sketchId);
			options.requestRender();
		} catch (error) {
			try {
				this.cancel();
			} catch (rollbackError) {
				throw new AggregateError([error, rollbackError], `Instant2D 预演及回滚失败：${state.target.dimensionId}`);
			}
			throw error;
		}
	}
}
